import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { CheckCircle, Chrome, Loader2, AlertCircle, Puzzle } from 'lucide-react';

export default function ExtensionConnect() {
  const [params] = useSearchParams();
  const extId = params.get('ext') || '';
  const [state, setState] = useState('connecting'); // connecting | done | error | no-ext
  const [detail, setDetail] = useState('');

  const connect = useCallback(async () => {
    if (!extId || !window.chrome?.runtime?.sendMessage) {
      setState('no-ext');
      return;
    }
    setState('connecting');
    try {
      const res = await api.post('/api/auth/extension-token');
      const { token, user } = res.data;
      window.chrome.runtime.sendMessage(
        extId,
        { type: 'LOCKENE_CONNECT', token, apiBaseUrl: window.location.origin, user },
        (resp) => {
          if (window.chrome.runtime.lastError || !resp?.success) {
            setDetail(window.chrome.runtime.lastError?.message || 'Extension did not respond');
            setState('error');
          } else {
            setState('done');
          }
        }
      );
    } catch (e) {
      setDetail(e.response?.data?.detail || e.message);
      setState('error');
    }
  }, [extId]);

  useEffect(() => { connect(); }, [connect]);

  return (
    <div className="max-w-md mx-auto mt-16 text-center px-6" data-testid="extension-connect-page">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 mb-5">
        <Puzzle className="h-7 w-7 text-primary" />
      </div>
      <h1 className="text-2xl font-bold mb-2">Connect LinkedIn Extension</h1>

      {state === 'connecting' && (
        <div className="flex items-center justify-center gap-2 text-muted-foreground mt-6" data-testid="ext-connecting">
          <Loader2 className="h-4 w-4 animate-spin" /> Handing your session to the extension…
        </div>
      )}

      {state === 'done' && (
        <div className="mt-6" data-testid="ext-connected">
          <div className="inline-flex items-center gap-2 text-emerald-500 font-medium">
            <CheckCircle className="h-5 w-5" /> Extension connected!
          </div>
          <p className="text-sm text-muted-foreground mt-3">
            You can close this tab. Open the extension on LinkedIn — it's already signed in as you.
          </p>
        </div>
      )}

      {state === 'error' && (
        <div className="mt-6" data-testid="ext-error">
          <div className="inline-flex items-center gap-2 text-red-500 font-medium">
            <AlertCircle className="h-5 w-5" /> Couldn't reach the extension
          </div>
          <p className="text-sm text-muted-foreground mt-2">{detail}</p>
          <Button className="mt-4" onClick={connect} data-testid="ext-retry-btn">Try again</Button>
        </div>
      )}

      {state === 'no-ext' && (
        <div className="mt-6" data-testid="ext-missing">
          <div className="inline-flex items-center gap-2 text-amber-500 font-medium">
            <Chrome className="h-5 w-5" /> Open this page from the extension
          </div>
          <p className="text-sm text-muted-foreground mt-3">
            Install the Lockene LinkedIn extension, click its icon, then press <strong>Connect to Lockene</strong> — it will bring you here automatically.
          </p>
        </div>
      )}
    </div>
  );
}
